import { useLayoutEffect, useRef, useState } from 'react';
import { breakpoints } from 'styles';
import { useReduxState } from 'hooks';

import BoxTableStickyHeader from './BoxTableStickyHeader';

interface Props {
  children: React.ReactNode;
  sticky?: boolean;
  style?: React.CSSProperties;
  useDarkerHighlight?: boolean;
  withStickyHeader?: boolean;
}

const BoxOverflowWrapper = ({
  children,
  style,
  useDarkerHighlight,
  withStickyHeader = false
}: Props) => {
  const { theme } = useReduxState();

  const tableCloneRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [shadowOffsetLeft, setShadowOffsetLeft] = useState(0);
  const [shadowOffsetRight, setShadowOffsetRight] = useState(0);

  useLayoutEffect(() => {
    let observer;
    let stickyHeader;

    const tableEl = wrapperRef.current?.querySelector(
      '.box-table'
    ) as HTMLDivElement;

    if (withStickyHeader && tableEl && tableCloneRef.current) {
      stickyHeader = new BoxTableStickyHeader(tableEl, tableCloneRef.current, {
        max: 0
      });
    }

    if (tableEl) {
      observer = new ResizeObserver(() => {
        let offsetLeft = 0;
        let offsetRight = 0;
        const stickyColumnsLeft = tableEl.querySelectorAll(
          '.box-table-header .box-table-cell.sticky-left'
        );
        const stickyColumnsRight = tableEl.querySelectorAll(
          '.box-table-header .box-table-cell.sticky-right'
        );
        stickyColumnsLeft.forEach((column) => {
          offsetLeft += column.getBoundingClientRect().width;
        });
        stickyColumnsRight.forEach((column) => {
          offsetRight += column.getBoundingClientRect().width;
        });
        if (offsetLeft !== shadowOffsetLeft) {
          setShadowOffsetLeft(offsetLeft);
        }
        if (offsetRight !== shadowOffsetRight) {
          setShadowOffsetRight(offsetRight);
        }
      });

      observer.observe(tableEl);
    }

    return () => {
      if (stickyHeader) stickyHeader.destroy();
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <>
      <div
        className={
          `overflow-wrapper` +
          `${useDarkerHighlight ? ' overflow-wrapper-darker' : ''}`
        }
        ref={wrapperRef}
        style={style}
      >
        {children}

        <style jsx>{`
          .overflow-wrapper {
            background: linear-gradient(
                90deg,
                white 30%,
                rgba(255, 255, 255, 0)
              ),
              linear-gradient(90deg, rgba(255, 255, 255, 0), white 70%) 0 100%,
              linear-gradient(
                90deg,
                rgba(0, 30, 60, 0.1),
                rgba(0, 30, 60, 0.05),
                rgba(0, 30, 60, 0)
              ),
              linear-gradient(
                90deg,
                rgba(0, 30, 60, 0),
                rgba(0, 30, 60, 0.05),
                rgba(0, 30, 60, 0.1)
              );
          }
          .overflow-wrapper-darker {
            background: linear-gradient(
                90deg,
                white 30%,
                rgba(255, 255, 255, 0)
              ),
              linear-gradient(90deg, rgba(255, 255, 255, 0), white 70%) 0 100%,
              linear-gradient(
                90deg,
                rgba(0, 30, 60, 0.2),
                rgba(0, 30, 60, 0.1),
                rgba(0, 30, 60, 0)
              ),
              linear-gradient(
                90deg,
                rgba(0, 30, 60, 0),
                rgba(0, 30, 60, 0.1),
                rgba(0, 30, 60, 0.2)
              );
          }
          .overflow-wrapper {
            background-attachment: local, local, scroll, scroll;
            background-color: white;
            background-position-x: 0px, 100%, 1px, 100%;
            background-repeat: no-repeat;
            background-size: 40px 100%, 40px 100%, 20px 100%, 20px 100%;
            border-radius: 0 0 ${theme.cardBorderRadius}
              ${theme.cardBorderRadius};
            overflow-x: auto;
          }
          @media (min-width: ${breakpoints.medium}px) {
            .overflow-wrapper {
              background-position-x: ${shadowOffsetLeft}px,
                calc(100% - ${shadowOffsetRight}px), ${shadowOffsetLeft + 1}px,
                calc(100% - ${shadowOffsetRight}px);
            }
          }
        `}</style>
      </div>

      {withStickyHeader ? (
        <div
          className="overflow-wrapper"
          style={{
            overflowX: 'auto',
            zIndex: 3
          }}
        >
          <div
            className="box-table"
            ref={tableCloneRef}
            style={{ borderCollapse: 'collapse' }}
          />
        </div>
      ) : null}
    </>
  );
};

export default BoxOverflowWrapper;
